import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as yup from "yup";

import { createAxiosInstance } from "api/axiosConfig";
import { logoutFromHelper } from "api/Helper";
import { PRACTCE_DETAILS_CONTROLER_URL } from "api/Service";
import { pathGenerators } from "utils/routes";
import {
  formatEircode,
  isAcceptedPhoneNumber,
  PHONE_NUMBER_ERROR,
  validateEircode,
} from "utils/validators";

import { logout } from "../../../../redux/auth/AuthSlice";
import { removeuser } from "../../../../redux/user/userSlice";
import { toPracticeDetailsPayload } from "../utils/practiceDetailsPayload";

const INITIAL_VALUES = {
  name: "",
  country: "",
  type: "",
  county: "",
  city: "",
  address: "",
  address2: "",
  address3: "",
  eircode: "",
  mainPhone: "",
  phone2: "",
  outOfHoursPhone: "",
  practiceEmail: "",
  website: "",
  twitter: "",
  smsName: "",
  facebook: "",
  registeredPracticeId: "",
  id: "",
  addressId: "",
  latitude: "",
  longitude: "",
  geoHash: "",
};

const validationSchema = yup.object({
  name: yup
    .string()
    .min(2, "Too Short!")
    .max(150, "Too Long!")
    .required("This field is required"),
  country: yup.string().required("This field is required"),
  city: yup.string().optional(),
  county: yup.string().required("This field is required"),
  eircode: yup
    .string()
    .required("This field is required")
    .test(
      "valid-eircode",
      "Invalid Eircode format. Expected format: A12BC34 or D6W1234",
      function (value) {
        if (!value) return true;

        return validateEircode(value).isValid;
      }
    ),
  address: yup.string().required("This field is required"),
  address2: yup.string().required("This field is required"),
  type: yup.string().required("Practice Type is required"),
  outOfHoursPhone: yup
    .string()
    .test("valid-phone", PHONE_NUMBER_ERROR, isAcceptedPhoneNumber),

  practiceEmail: yup
    .string()
    .email("Must be a valid email")
    .max(255)
    .required("Email is required"),
});

/**
 * Everything Practice Details does with data: the Formik form and its yup schema,
 * loading the practice, the logo as base64, and the save request. The page and its
 * sub-components only render what this returns.
 */
export const usePracticeDetailsForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  let tenantVal = useSelector((state) => state.auth.user?.tenant);
  if (!tenantVal) tenantVal = localStorage.getItem("kinesin-tenant");

  const [base64Image, setBase64Image] = useState("");
  const [displayImage, setDisplayImage] = useState(null);

  const errorHandling = (error) => {
    console.error(error);
    toast.error(error.message);
    if (error && error.response && error.response.status) {
      if (error.response.status === 403) {
        logoutFromHelper();
        dispatch(logout());
        dispatch(removeuser());
        navigate(pathGenerators.auth.logout());
      }
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (state) => {
      const axiosInstance = createAxiosInstance();

      const requestData = toPracticeDetailsPayload({
        values: state,
        tenant: tenantVal,
        practicePicture: base64Image,
      });

      await axiosInstance.post(
        `${tenantVal}${PRACTCE_DETAILS_CONTROLER_URL}`,
        requestData,
        { headers: { "Content-Type": "application/json" } }
      );

      return state;
    },
    onSuccess: (state) => {
      toast.success(
        state.id
          ? "Details updated successfully"
          : "New Details created successfully"
      );
    },
    onError: (error) => {
      errorHandling(error);
    },
  });

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: (state) => saveMutation.mutate(state),
  });

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}${PRACTCE_DETAILS_CONTROLER_URL}`
        );
        const practiceData = response.data;
        const imageData = practiceData[0].practicePicture;
        setBase64Image(imageData);
        setDisplayImage(imageData);
        if (practiceData !== undefined && practiceData.length > 0) {
          formik.setFieldValue("name", practiceData[0].name);
          formik.setFieldValue("mainPhone", practiceData[0].mainPhone);
          formik.setFieldValue("phone2", practiceData[0].phone2 || "");
          formik.setFieldValue(
            "outOfHoursPhone",
            practiceData[0].outOfHoursPhone || ""
          );
          formik.setFieldValue("type", practiceData[0].description);
          formik.setFieldValue("practiceEmail", practiceData[0].practiceEmail);
          formik.setFieldValue("facebook", practiceData[0].facebook);
          formik.setFieldValue("website", practiceData[0].website);
          formik.setFieldValue("smsName", practiceData[0].smsName);
          formik.setFieldValue("twitter", practiceData[0].twitter);
          formik.setFieldValue(
            "registeredPracticeId",
            practiceData[0].registeredPracticeId
          );
          formik.setFieldValue("id", practiceData[0].id);
          formik.setFieldValue("addressId", practiceData[0].addressId);

          if (
            practiceData[0].address &&
            practiceData[0].address.postCode !== undefined
          )
            formik.setFieldValue(
              "eircode",
              formatEircode(practiceData[0].address.postCode) ||
                practiceData[0].address.postCode
            );
          if (
            practiceData[0].address &&
            practiceData[0].address.addressLine1 !== undefined
          )
            formik.setFieldValue(
              "address",
              practiceData[0].address.addressLine1
            );
          if (
            practiceData[0].address &&
            practiceData[0].address.addressLine2 !== undefined
          )
            formik.setFieldValue(
              "address2",
              practiceData[0].address.addressLine2
            );
          if (
            practiceData[0].address &&
            practiceData[0].address.addressLine3 !== undefined
          )
            formik.setFieldValue(
              "address3",
              practiceData[0].address.addressLine3
            );
          if (
            practiceData[0].address &&
            practiceData[0].address.city !== undefined
          )
            formik.setFieldValue("city", practiceData[0].address.city);
          if (
            practiceData[0].address &&
            practiceData[0].address.country !== undefined
          )
            formik.setFieldValue("country", practiceData[0].address.country);
          if (
            practiceData[0].address &&
            practiceData[0].address.county !== undefined
          )
            formik.setFieldValue("county", practiceData[0].address.county);
          if (
            practiceData[0].address &&
            practiceData[0].address.latitude !== undefined
          )
            formik.setFieldValue("latitude", practiceData[0].address.latitude);
          if (
            practiceData[0].address &&
            practiceData[0].address.longitude !== undefined
          )
            formik.setFieldValue(
              "longitude",
              practiceData[0].address.longitude
            );
          if (
            practiceData[0].address &&
            practiceData[0].address.geoHash !== undefined
          )
            formik.setFieldValue("geoHash", practiceData[0].address.geoHash);
        }
      } catch (error) {
        errorHandling(error);
      }
    };
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantVal]);

  const selectLogo = (file) => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      setBase64Image(event.target.result);
    };

    reader.readAsDataURL(file);
  };

  /*
   * Google hands back the picked place as loose components; the same five fields plus
   * the coordinates are written whether the place came from the proxy or the JS API.
   */
  const applyResolvedAddress = (addressData) => {
    const addressComponents = addressData.address_components;
    const location = addressData.geometry.location;

    let streetNumber = "";
    let route = "";
    let locality = "";
    let administrativeArea = "";
    let country = "";
    let postalCode = "";

    addressComponents.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number")) {
        streetNumber = component.long_name;
      } else if (types.includes("route")) {
        route = component.long_name;
      } else if (types.includes("locality")) {
        locality = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        administrativeArea = component.long_name;
      } else if (types.includes("country")) {
        country = component.long_name;
      } else if (types.includes("postal_code")) {
        postalCode = component.short_name;
      }
    });

    const address1 =
      streetNumber && route
        ? `${streetNumber} ${route}`
        : route || streetNumber;
    formik.setFieldValue("address", address1);
    formik.setFieldValue("city", locality);
    formik.setFieldValue("county", administrativeArea.toLowerCase());
    formik.setFieldValue("country", country);
    formik.setFieldValue("eircode", postalCode);

    if (location.lat && location.lng) {
      formik.setFieldValue("latitude", location.lat);
      formik.setFieldValue("longitude", location.lng);
    } else if (typeof location.lat === "function") {
      formik.setFieldValue("latitude", location.lat());
      formik.setFieldValue("longitude", location.lng());
    }
  };

  const changeEircode = (newEircode) => {
    formik.setFieldValue("eircode", newEircode);
  };

  const changeLocation = (newLocation) => {
    formik.setFieldValue("latitude", newLocation.lat);
    formik.setFieldValue("longitude", newLocation.lng);
    if (newLocation.geoHash) {
      formik.setFieldValue("geoHash", newLocation.geoHash);
    }
  };

  const fullAddress = [
    formik.values.address,
    formik.values.address2,
    formik.values.address3,
    formik.values.city,
    formik.values.county,
    formik.values.country,
  ]
    .filter(Boolean)
    .join(", ");

  return {
    formik,
    base64Image,
    displayImage,
    selectLogo,
    applyResolvedAddress,
    changeEircode,
    changeLocation,
    fullAddress,
    isSaving: saveMutation.isPending,
  };
};
